import React, { useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getTimelineData,
  getVehicleTrackingWorkflows,
  getWorkflowStagesForWorkflow,
} from 'src/redux/Actions/VehicleTracking';
import { ReactComponent as WorkflowIcon } from 'src/Images/icons/common/workflowIcon.svg';

function SelectWorkflow({
  selectedWorkflowObj,
  setSelectedWorkflowObj,
  selectedStageObj,
  setSelectedStageObj,
  selectedStep,
  setSelectedStep,
  totalSteps,
}) {
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const {
    timeline: timelineData,
    workflows: availableWorkflows,
    workflowStagesForWorkflow,
    vehicleTrackingDetails,
  } = vehicleTrackingDataReducer;
  const vehicleTrackingNumber = vehicleTrackingDetails?.vehicleTrackingNumber;

  const [
    workflowStageSelectionErrorMessage,
    setWorkflowStageSelectionErrorMessage,
  ] = useState('');

  useEffect(() => {
    if (vehicleTrackingNumber && tenantOrganisationId) {
      dispatch(getTimelineData(vehicleTrackingNumber, tenantOrganisationId));
    }
  }, [dispatch, vehicleTrackingNumber, tenantOrganisationId]);

  useEffect(() => {
    if (tenantOrganisationId) {
      dispatch(
        getVehicleTrackingWorkflows(
          tenantOrganisationId,
          vehicleTrackingDetails?.direction?.id,
        ),
      );
    }
  }, [dispatch, tenantOrganisationId]);

  useEffect(() => {
    if (tenantOrganisationId && selectedWorkflowObj) {
      dispatch(
        getWorkflowStagesForWorkflow(
          tenantOrganisationId,
          selectedWorkflowObj?.id,
        ),
      );
    }
  }, [dispatch, tenantOrganisationId, selectedWorkflowObj]);

  const handleWorkflowSelect = (workflow) => {
    setSelectedWorkflowObj({
      id: workflow.id,
      name: workflow.name,
    });
    setSelectedStageObj(null);
    setWorkflowStageSelectionErrorMessage('');
  };

  // this iterates over all the previous mandatory workflow stages and checks if target vs actual exists
  const getIsWorkflowStageSelectable = (stageIndex) => {
    for (let i = 0; i < stageIndex; i += 1) {
      const stage = workflowStagesForWorkflow?.[i];

      if (stage?.mandatory) {
        const timelineWorkflowStageObj = timelineData?.timeline?.find(
          (timelineItem) => timelineItem.stage_id === stage.stage_id,
        );

        if (!timelineWorkflowStageObj?.target_vs_actual_exists) {
          return stage;
        }
      }
    }

    return null;
  };

  const handleWorkflowStageSelect = (station, stageIndex) => {
    const isWorkflowStageSelectable = getIsWorkflowStageSelectable(stageIndex);

    if (isWorkflowStageSelectable) {
      setWorkflowStageSelectionErrorMessage(
        `Station Error: ${isWorkflowStageSelectable?.name} station is a mandatory station you cannot skip it.`,
      );
      return;
    }

    setWorkflowStageSelectionErrorMessage('');
    setSelectedStageObj({
      id: station.id,
      name: station.name,
    });
  };

  function handlePreviousStep() {
    setSelectedStep(selectedStep - 1);
  }

  function handleNextStep() {
    setSelectedStep(selectedStep + 1);
  }

  return (
    <>
      <div
        className={`select-workflow-station-container vertical-padding-10 horizontal-padding-10 theme-scrollable-container-${themeColor}`}
      >
        <div className="duplicate-current-flow">
          <span className="duplicate-current-flow-label">Current Flow</span>
          <div
            className="duplicate-current-flow-steps"
            style={{
              gridTemplateColumns: `repeat(${Math.max(
                timelineData?.timeline?.length || 1,
                1,
              )}, minmax(58px, 1fr))`,
            }}
          >
            {timelineData?.timeline?.map((station) => {
              const isComplete = station?.completed;
              const isActive =
                station?.workflow_stage_id === selectedStageObj?.id;
              const timelineKey = [
                station?.workflow_stage_id,
                station?.workflow_stage_name,
                station?.logs?.entry_timestamp || 'no-entry',
                station?.logs?.exit_timestamp || 'no-exit',
                station?.completed,
                station?.skipped,
              ].join('-');

              return (
                <div
                  className={`duplicate-current-flow-step ${
                    isComplete ? 'is-complete' : ''
                  } ${isActive ? 'is-active' : ''}`}
                  key={timelineKey}
                >
                  {station?.skipped && (
                    <div className="duplicate-current-flow-status-pill status-pill red">
                      <span className="text-xs">Skipped</span>
                    </div>
                  )}
                  <span className="duplicate-current-flow-dot" />
                  <span className="duplicate-current-flow-name">
                    {station.workflow_stage_name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <Row className=" g-3 padding-top-20">
          <Col md={6}>
            <div className="select-workflow-panel">
              <div className="select-workflow-panel-title">
                Workflow Station
              </div>
              <div className="workflow-option-grid">
                {availableWorkflows?.map((workflow) => {
                  const isSelected = workflow.id === selectedWorkflowObj?.id;

                  return (
                    <button
                      type="button"
                      className={`workflow-option-card ${
                        isSelected ? 'is-selected' : ''
                      }`}
                      key={workflow.id}
                      onClick={() => handleWorkflowSelect(workflow)}
                    >
                      <div className="workflow-option-card-top">
                        <WorkflowIcon className="workflow-option-icon" />
                        {isSelected && workflowStagesForWorkflow?.length ? (
                          <span className="workflow-stage-badge">
                            Stage{' '}
                            {String(workflowStagesForWorkflow.length).padStart(
                              2,
                              '0',
                            )}
                          </span>
                        ) : null}
                      </div>
                      <div className="workflow-option-name">
                        {workflow.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </Col>

          <Col md={6}>
            <div className="select-workflow-panel">
              <div className="select-workflow-panel-title">Select Station</div>
              {workflowStageSelectionErrorMessage ? (
                <div className="station-option-error text-xs">
                  {workflowStageSelectionErrorMessage}
                </div>
              ) : null}
              <div className="station-option-list">
                {workflowStagesForWorkflow?.map((station, index) => {
                  const isSelected = station.id === selectedStageObj?.id;
                  const isBlocked = Boolean(
                    getIsWorkflowStageSelectable(index),
                  );
                  return (
                    <button
                      type="button"
                      className={`station-option-row ${
                        isSelected ? 'is-selected' : ''
                      } ${isBlocked ? 'is-blocked' : ''}`}
                      key={station.id}
                      onClick={() => handleWorkflowStageSelect(station, index)}
                    >
                      <span className="station-option-radio" />
                      <span className="station-option-name">
                        {station.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className=" div-flex-row div-space-between div-align-center vertical-padding-10 horizontal-padding-10 border-top">
        <button
          type="button"
          className={`common-btn-outlined theme-border-color-${themeColor}`}
          onClick={handlePreviousStep}
          disabled={selectedStep === 1}
        >
          Back
        </button>
        <button
          type="button"
          className={`common-btn theme-background-${themeColor}`}
          onClick={handleNextStep}
          disabled={selectedStep === totalSteps}
        >
          Next
        </button>
      </div>
    </>
  );
}

export default SelectWorkflow;
